import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Features.css';
import DifferentiatorSection from './DifferentiatorSection';
import WindowsFeature from './WindowsFeature';
import {
  ConversationalExplorationFeature,
  AISuggestionsFeature,
  DiscoveryProFeature,
  AITestHealingFeature,
  AIBugCreationFeature,
  AIBugSolvingFeature,
  GitHubCopilotFeature,
  KnowledgeBaseFeature,
  EnterpriseIntegrationsFeature,
  ReportsInsightsFeature,
  NoVendorLockInFeature,
  VisualModelFeature,
  MFAFeature,
  PRProcessingFeature
} from './features/index';

const Features = () => {
  const featuresRef = useRef(null);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;

    const showAllElements = () => {
      const elements = featuresRef.current?.querySelectorAll('.features-item');
      elements?.forEach((el) => {
        el.classList.add('visible');
      });
    };

    if (isMobile) {
      showAllElements();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -20px 0px'
      }
    );

    const elements = featuresRef.current?.querySelectorAll('.features-item');
    if (elements && elements.length > 0) {
      elements.forEach((el) => observer.observe(el));
    } else {
      setTimeout(showAllElements, 100);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    { ...ConversationalExplorationFeature, id: 'conversational-exploration' },
    { ...WindowsFeature, id: 'windows-desktop' },
    { ...AISuggestionsFeature, id: 'ai-suggestions' },
    { ...DiscoveryProFeature, id: 'discovery' },
    { ...AITestHealingFeature, id: 'ai-test-healing' },
    { ...AIBugCreationFeature, id: 'ai-bug-detection' },
    { ...AIBugSolvingFeature, id: 'ai-bug-solving' },
    { ...GitHubCopilotFeature, id: 'github-copilot' },
    { ...PRProcessingFeature, id: 'pr-processing' },
    { ...KnowledgeBaseFeature, id: 'knowledge-base' },
    { ...MFAFeature, id: 'mfa-totp' },
    { ...EnterpriseIntegrationsFeature, id: 'enterprise-integrations' },
    { ...ReportsInsightsFeature, id: 'reports-insights' },
    { ...VisualModelFeature, id: 'visual-model' },
    { ...NoVendorLockInFeature, id: 'no-vendor-lock-in' }
  ];

  return (
    <section className="features" id="features" ref={featuresRef}>
      <div className="features-container">
        {/* Differentiator Section */}
        <DifferentiatorSection />

        <div className="section-header">
          <h2>Powerful Features. Simple Conversations.</h2>
          <p>AI that understands what you want, creates tests while you talk, and lets you run them anywhere.</p>
        </div>

        {/* Essentials intro - How AI Connects Code and UI */}
        <div className="essentials-intro">
          <h3 className="essentials-title">But first, let us explain what's essential</h3>
          <p className="essentials-subtitle">
            The foundation that makes everything else easy,{' '}
            <span className="discovery-highlight-wrapper">
              <span className="discovery-highlight">Discovery!</span>
              <span className="discovery-callout">
                <svg className="discovery-callout-arrow" viewBox="0 0 60 40" fill="none">
                  <path d="M30 5 Q30 20 25 30 Q22 35 18 37" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
                  <path d="M22 40 L17 36 L15 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <span className="discovery-callout-text">Autonomous AI discovery of your application. It makes a map!</span>
              </span>
            </span>
          </p>

          <div className="essentials-content">
            {/* Discovery Visual with integrated labels */}
            <div className="discovery-visual-layout">
              {/* Left side - Code explanation */}
              <div className="discovery-side discovery-side-left">
                <span className="side-label">Code</span>
                <span className="side-text">Analyzes repos, APIs, data flows</span>
              </div>

              {/* Center - Wheel */}
              <div className="discovery-wheel-container">
                <div className="discovery-wheel">
                  <div className="wheel-track-ring" />
                  <div className="wheel-highlight-arc" />
                  <div className="wheel-center-hub">
                    <img src="/qatelogo.png" alt="Qate AI" className="wheel-hub-logo" loading="lazy" />
                  </div>
                </div>
              </div>

              {/* Right side - Interface explanation */}
              <div className="discovery-side discovery-side-right">
                <span className="side-label">Interface</span>
                <span className="side-text">Explores UI like real users</span>
              </div>
            </div>

            <div className="discovery-connects-label">Connects Code to UI</div>

            {/* Flow arrow down */}
            <div className="essentials-flow-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </svg>
            </div>

            {/* Discovery Result Card */}
            <div className="discovery-result">
              <div className="result-subtitle">Up to 30 workflows discovered</div>
              <div className="result-number">30-40</div>
              <div className="result-text"><strong>Real, executable tests</strong> generated automatically from your codebase and user flows. Ready to refine, run, and heal.</div>
            </div>

            {/* Incremental Discovery */}
            <div className="incremental-discovery-section">
              <div className="incremental-title">Then, grow your coverage incrementally</div>
              <div className="incremental-subtitle">Run lightweight discoveries to find more workflows and tests over time</div>

              <div className="coverage-growth">
                <div className="coverage-step">
                  <div className="coverage-card coverage-initial">
                    <div className="coverage-percent">60%</div>
                    <div className="coverage-label">Initial</div>
                  </div>
                  <div className="coverage-caption">First Discovery</div>
                </div>

                <div className="coverage-arrow">→</div>

                <div className="coverage-step">
                  <div className="coverage-card coverage-workflows">
                    <div className="coverage-percent">78%</div>
                    <div className="coverage-label">+18%</div>
                  </div>
                  <div className="coverage-caption">+Workflows</div>
                </div>

                <div className="coverage-arrow">→</div>

                <div className="coverage-step">
                  <div className="coverage-card coverage-tests">
                    <div className="coverage-percent">95%</div>
                    <div className="coverage-label">+17%</div>
                  </div>
                  <div className="coverage-caption">+Tests</div>
                </div>
              </div>
            </div>

            {/* Flow steps */}
            <div className="essentials-flow">
              <div className="flow-step">
                <span className="flow-number">1</span>
                <span className="flow-label">Analyze Code</span>
              </div>
              <div className="flow-connector">→</div>
              <div className="flow-step">
                <span className="flow-number">2</span>
                <span className="flow-label">Explore UI</span>
              </div>
              <div className="flow-connector">→</div>
              <div className="flow-step">
                <span className="flow-number">3</span>
                <span className="flow-label">Map Workflows</span>
              </div>
              <div className="flow-connector">→</div>
              <div className="flow-step">
                <span className="flow-number">4</span>
                <span className="flow-label">Generate Tests</span>
              </div>
              <div className="flow-connector">→</div>
              <div className="flow-step">
                <span className="flow-number">5</span>
                <span className="flow-label">Run & Heal</span>
              </div>
            </div>

          </div>
        </div>

        {/* Journey start indicator */}
        <div className="features-journey-start">
          <span className="journey-text">Let's explore what's possible</span>
          <div className="journey-arrow">↓</div>
        </div>

        <div className="features-content-list">
          {/* Curved connecting line */}
          <svg className="features-connector" viewBox="0 -50 100 1250" preserveAspectRatio="none">
            <path
              d="M 50 -50 L 50 0 C 0 100, 100 200, 50 300 C 0 400, 100 500, 50 600 C 0 700, 100 800, 50 900 C 0 1000, 100 1100, 50 1200"
              fill="none"
              stroke="currentColor"
            />
          </svg>
          {features.map((feature, index) => (
            <div
              key={index}
              id={feature.id}
              className={`features-item ${index % 2 === 0 ? 'left' : 'right'} ${index === 1 ? 'windows-desktop-feature' : ''}`}
            >
              {index === 1 && <div className="card-background"></div>}
              <div className="feature-content">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                {feature.highlight && (
                  Array.isArray(feature.highlight) ? (
                    <div className="feature-highlights">
                      {feature.highlight.map((h, i) => (
                        <span key={i} className="feature-highlight">{h}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="feature-highlight">{feature.highlight}</span>
                  )
                )}
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                {feature.docsLink && (
                  <Link to={feature.docsLink.url} className="feature-docs-link">
                    {feature.docsLink.label}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                )}
                {feature.features && (
                  <ul className="feature-list">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="feature-list-item">
                        <div className="feature-list-icon">{item.icon}</div>
                        <div className="feature-list-text" dangerouslySetInnerHTML={{ __html: item.text }}></div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="feature-visual">
                {feature.animation}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
